import { useState, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Play, Mic2, Guitar, Sparkles } from "lucide-react";
import SpecsBar from "./SpecsBar";
import { VIDEO_ID, EMBED_URL, THUMB_URL, THUMB_FALLBACK } from "../constants";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Phone() {
  const [playing, setPlaying] = useState(false);
  const [thumb, setThumb] = useState(THUMB_URL);

  return (
    <div className="phone-shell relative w-[272px] sm:w-[312px] lg:w-[340px] aspect-[9/19] rounded-[3rem] p-[10px]" data-testid="hero-phone-mockup">
      <div className="relative w-full h-full rounded-[2.4rem] overflow-hidden bg-black">
        <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-[22px] bg-black rounded-full z-20 border border-white/10" />
        {playing ? (
          <iframe
            src={EMBED_URL}
            title="Hulda Perez — Video Reel"
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="hero-reel-iframe"
          />
        ) : (
          <>
            <img
              src={thumb}
              onError={() => setThumb(THUMB_FALLBACK)}
              alt="Hulda Perez video reel"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-reel-thumbnail"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/40" />
            <motion.button
              onClick={() => setPlaying(true)}
              whileHover={{ scale: 1.12 }}
              whileTap={{ scale: 0.94 }}
              className="absolute inset-0 m-auto w-16 h-16 lg:w-[72px] lg:h-[72px] rounded-full bg-hpink text-white flex items-center justify-center glow-pink z-10"
              aria-label="Play the reel"
              data-testid="hero-play-reel"
            >
              <Play size={26} fill="currentColor" className="ml-1" />
            </motion.button>
            <div className="absolute bottom-4 left-4 right-4 z-10 flex items-end justify-between">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] text-hblue">LIVE PERFORMANCE REEL</p>
                <p className="font-display font-extrabold text-white text-sm mt-1 tracking-wide">HULDA PEREZ</p>
              </div>
              <span className="font-mono text-[10px] text-white/70 border border-white/20 rounded-full px-2.5 py-1">
                2011—NOW
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [9, -9]), { stiffness: 140, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-11, 11]), { stiffness: 140, damping: 16 });

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen overflow-hidden bg-grid"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width);
        my.set((e.clientY - r.top) / r.height);
      }}
      onMouseLeave={() => {
        mx.set(0.5);
        my.set(0.5);
      }}
      data-testid="hero-section"
    >
      <motion.div style={{ opacity: glowOpacity }} className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-hpink/25 blur-[140px]" />
        <div className="absolute top-1/3 -right-32 w-[520px] h-[520px] rounded-full bg-hblue/20 blur-[140px]" />
        <div className="absolute bottom-0 left-1/3 w-[480px] h-[480px] rounded-full bg-horange/20 blur-[150px]" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-16 pt-28 lg:pt-36 pb-14 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-6 items-center">
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.35em] text-hblue mb-6"
          >
            Electronic Press Kit — Vocalist • Guitarist • Songwriter
          </motion.p>

          <h1 className="font-display font-black uppercase leading-[0.85] tracking-tighter">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block text-[clamp(3.6rem,11vw,9rem)] text-white"
                variants={line}
                initial="hidden"
                animate="show"
                custom={0}
              >
                HULDA
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block text-[clamp(3.6rem,11vw,9rem)] text-stroke"
                variants={line}
                initial="hidden"
                animate="show"
                custom={1}
              >
                PEREZ
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 flex flex-wrap items-center gap-3"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-hpink/40 bg-hpink/10 px-4 py-2 text-xs sm:text-sm font-semibold text-white">
              <Mic2 size={14} className="text-hpink" /> Mezzo-Soprano with Strong Belt
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-hblue/40 bg-hblue/10 px-4 py-2 text-xs sm:text-sm font-semibold text-white">
              <Guitar size={14} className="text-hblue" /> Self-Accompanied Acoustic
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#media"
              data-testid="hero-watch-reel"
              className="inline-flex items-center gap-2 rounded-full bg-hpink hover:bg-horange transition-colors duration-300 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold glow-pink"
            >
              <Play size={14} fill="currentColor" /> Watch the Reel
            </a>
            <a
              href="#booking"
              data-testid="hero-book-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-hpink hover:text-hpink transition-all duration-300 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] font-bold"
            >
              <Sparkles size={14} /> Book Hulda
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8 font-mono text-[11px] tracking-[0.25em] text-hdim uppercase"
          >
            As seen on NBC&rsquo;s The Voice — Season 1
          </motion.p>
        </motion.div>

        <div className="relative flex justify-center lg:justify-end [perspective:1200px]">
          <motion.div
            style={{ y: phoneY, rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="relative"
          >
            <Phone />
            <span className="floaty absolute -left-16 sm:-left-24 top-16 hidden sm:inline-flex items-center gap-2 rounded-full glass px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-hblue">
              MEZZO-SOPRANO
            </span>
            <span className="floaty-slow absolute -right-12 sm:-right-20 top-1/3 hidden sm:inline-flex items-center gap-2 rounded-full glass px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-horange">
              STRONG BELT
            </span>
            <span className="floaty absolute -left-10 sm:-left-20 bottom-24 hidden sm:inline-flex items-center gap-2 rounded-full glass px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-hpink">
              GUITAR + VOX
            </span>
          </motion.div>
        </div>
      </div>

      <SpecsBar />
    </section>
  );
}
